import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Ban, Clock3, Ellipsis, KeyRound, TrendingUp } from "lucide-react";
import { Menu } from "@base-ui/react/menu";
import {
  Link,
  UNSAFE_DataRouterContext,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { useTranslation } from "react-i18next";
import { adminApi, ApiError, apiErrorMessage } from "@/lib/api";
import {
  activationIDFromFocus,
  licenseDetailTab,
  productKeyIDFromFocus,
  type LicenseDetailTab,
} from "@/lib/license-detail-tab";
import { licenseSeatSnapshot, licenseValiditySnapshot } from "@/lib/license-overview-stats";
import { adminLicensePath } from "@/lib/navigation";
import {
  readDeviceLimitHandoff,
  readIssueKeysRestore,
  type IssueKeysDraft,
} from "@/lib/device-capacity-handoff";
import {
  clearProductKeyReplacementRecovery,
  readProductKeyReplacementRecovery,
  writeProductKeyReplacementRecovery,
} from "@/lib/product-key-replacement-recovery";
import { can, requiredRole } from "@/lib/permissions";
import {
  queryKeys,
  useActivationsQuery,
  useCurrentIdentityQuery,
  useLicenseTimelineQuery,
  useProductKeyActivationsQuery,
  useProductKeysQuery,
  useSearchQuery,
} from "@/lib/queries";
import { useTimezone } from "@/lib/timezone";
import { formatMaskedProductKey } from "@/lib/product-key-display";
import { cn, humanizeAction } from "@/lib/utils";
import type {
  Activation,
  AdminRole,
  AuditEvent,
  KeyBatchResponse,
  ProductKeyMetadata,
  SearchResult,
} from "@/lib/types";
import type { ProductKeyReplacement } from "@/lib/generated/model";
import { EditLicenseFormContainer } from "@/components/admin/license-edit-form";
import { CopyIdButton } from "@/components/admin/copy-id-button";
import { EmptyState, InlineAlert, LoadingRows } from "@/components/admin/feedback";
import { AuditChangeEvidence } from "@/components/admin/license-business-summary";
import { IssueKeysDialog } from "@/components/admin/issue-keys-dialog";
import { PageFrame } from "@/components/admin/page-frame";
import { PageHeader } from "@/components/admin/page-header";
import { PromoteLicenseDialog, isPromotableTrial, type PromoteLicensePayload } from "@/components/admin/promote-license-dialog";
import {
  createReplacementIdempotencyKey,
  ReplaceProductKeyDialog,
  type ProductKeyReplacementAttempt,
} from "@/components/admin/replace-product-key-dialog";
import { DeactivateActivationDialog } from "@/components/admin/deactivate-activation-dialog";
import { RevokeLicenseDialog } from "@/components/admin/revoke-license-dialog";
import { RevokeProductKeyDialog } from "@/components/admin/revoke-product-key-dialog";
import { LicenseStatusBadge, PermissionHint } from "@/components/admin/status";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function LicenseDetailPage() {
  const { t } = useTranslation();
  const { licenseId } = useParams<{ licenseId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const identityQuery = useCurrentIdentityQuery();
  const roles = identityQuery.data?.roles;
  const focus = searchParams.get("focus") ?? "";
  const focusedActivationID = activationIDFromFocus(focus);
  const detailTab = licenseDetailTab(searchParams.get("tab"), focus);
  const searchQuery = useSearchQuery(licenseId ?? "");
  const result = searchQuery.data?.results.find((item) => item.license.id === licenseId);
  const focusedActivation = focusedActivationID
    ? result?.activations.find((activation) => activation.id === focusedActivationID)
    : undefined;
  const loadingLicense =
    searchQuery.isPending || (searchQuery.isFetching && searchQuery.isPlaceholderData);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [formRevision, setFormRevision] = useState(0);
  const [configurationSaved, setConfigurationSaved] = useState<{ licenseId: string; at: string } | null>(null);
  const handleConfigurationDirty = useCallback((dirty: boolean) => {
    setSettingsDirty(dirty);
    if (dirty) setConfigurationSaved(null);
  }, []);
  const resetConfiguration = (savedAt?: string) => {
    flushSync(() => {
      setSettingsDirty(false);
      setConfigurationSaved(savedAt && licenseId ? { licenseId, at: savedAt } : null);
      setFormRevision((revision) => revision + 1);
    });
  };
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [blockedNav, setBlockedNav] = useState<{ proceed: () => void; reset: () => void } | null>(null);
  const deviceLimitHandoff = readDeviceLimitHandoff(location.state, licenseId ?? "");
  const requestedTab = searchParams.get("tab");
  const indexReturnTo = readIndexReturnTo(location.state);
  useEffect(() => {
    if (!deviceLimitHandoff || requestedTab || focus) return;
    navigate(
      { pathname: location.pathname, search: "?tab=configuration" },
      { replace: true, state: location.state },
    );
  }, [deviceLimitHandoff, focus, location.pathname, location.state, navigate, requestedTab]);
  const restoreIssueKeysState = deviceLimitHandoff
    ? {
        kind: "restore-issue-keys" as const,
        licenseId: deviceLimitHandoff.licenseId,
        draft: deviceLimitHandoff.draft,
        indexReturnTo,
      }
    : undefined;

  const leaveToIndex = () => {
    navigate(indexReturnTo ?? "/admin/licenses");
  };
  const cancelHandoff = () => {
    if (!licenseId || !deviceLimitHandoff) {
      leaveToIndex();
      return;
    }
    navigate(
      { pathname: adminLicensePath(licenseId), search: "?tab=seats" },
      { replace: true, state: restoreIssueKeysState },
    );
  };
  const requestLeave = () => {
    if (settingsDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    if (deviceLimitHandoff) {
      cancelHandoff();
      return;
    }
    leaveToIndex();
  };
  const confirmDiscard = () => {
    resetConfiguration();
    setDiscardConfirmOpen(false);
    if (blockedNav) {
      const proceed = blockedNav.proceed;
      setBlockedNav(null);
      proceed();
      return;
    }
    if (deviceLimitHandoff) {
      cancelHandoff();
      return;
    }
    leaveToIndex();
  };
  const keepEditing = () => {
    const reset = blockedNav?.reset;
    setBlockedNav(null);
    setDiscardConfirmOpen(false);
    reset?.();
  };

  const title = result
    ? result.license.alias?.trim() || result.customer.name
    : t("search.details.licenseDetail", "License details");
  const description = result
    ? result.license.alias?.trim()
      ? `${result.customer.name} · ${result.product.name}`
      : result.product.name
    : t("licenses.editDescription", "Update status, expiry, Entitlements, and Device Policy. Every change is audited.");

  return (
    <PageFrame size="fluid">
      {(!result || loadingLicense || searchQuery.isError) && (
        <PageHeader
          title={title}
          titleClassName="text-balance"
          description={description}
          actions={
            <Button variant="ghost" onClick={requestLeave}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              {t("common.back", "Back to licenses")}
            </Button>
          }
        />
      )}
      {loadingLicense ? (
        <div className="rounded-lg border border-line bg-panel p-6">
          <LoadingRows count={3} />
        </div>
      ) : searchQuery.isError ? (
        <InlineAlert
          title={t("licenses.loadErrorTitle", "License could not load")}
          message={t("licenses.loadErrorDescription", "Try again after checking the Admin API connection.")}
          onRetry={() => void searchQuery.refetch()}
        />
      ) : !result || !licenseId ? (
        <EmptyState
          title={t("licenses.notFoundTitle", "License not found")}
          message={t("licenses.notFoundDescription", "No License record matching ID “{{id}}” was found.", { id: licenseId })}
          action={
            <Button variant="outline" onClick={() => navigate("/admin/licenses")}>
              {t("common.back", "Back to licenses")}
            </Button>
          }
        />
      ) : (
        <LicenseDetailBody
          formRevision={formRevision}
          savedAt={configurationSaved?.licenseId === result.license.id ? configurationSaved.at : undefined}
          notifyOnSave={Boolean(deviceLimitHandoff)}
          result={result}
          focus={focus}
          tab={detailTab}
          focusedActivation={focusedActivation}
          roles={roles}
          suggestedMaxDevices={deviceLimitHandoff?.suggestedLimit}
          initialReason={deviceLimitHandoff?.draft.reason}
          indexReturnTo={indexReturnTo}
          onDirtyChange={handleConfigurationDirty}
          onCancel={requestLeave}
          onRefresh={() => void searchQuery.refetch()}
          onSaved={() => {
            resetConfiguration(new Date().toISOString());
            if (deviceLimitHandoff) {
              navigate(
                { pathname: adminLicensePath(result.license.id), search: "?tab=seats" },
                { replace: true, state: restoreIssueKeysState },
              );
              return;
            }
            navigate(
              { pathname: adminLicensePath(result.license.id), search: location.search },
              { replace: true, state: indexReturnTo ? { indexReturnTo } : null },
            );
          }}
        />
      )}
      <DirtyNavigationGuard dirty={settingsDirty} onBlocked={setBlockedNav} />
      <Dialog
        open={discardConfirmOpen || Boolean(blockedNav)}
        onOpenChange={(open) => {
          if (open) {
            setDiscardConfirmOpen(true);
            return;
          }
          keepEditing();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("search.details.discardTitle", "Discard unsaved changes?")}</DialogTitle>
            <DialogDescription>{t("search.details.discardDescription", "Your edits have not been saved. Leaving now discards them.")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={keepEditing}>
              {t("search.details.keepEditing", "Keep editing")}
            </Button>
            <Button variant="danger" onClick={confirmDiscard}>
              {t("search.details.discardChanges", "Discard changes")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageFrame>
  );
}

function LicenseDetailBody({
  formRevision,
  savedAt,
  notifyOnSave,
  result,
  focus,
  tab,
  focusedActivation,
  roles,
  suggestedMaxDevices,
  initialReason,
  indexReturnTo,
  onDirtyChange,
  onCancel,
  onRefresh,
  onSaved,
}: {
  formRevision: number;
  savedAt?: string;
  notifyOnSave: boolean;
  result: SearchResult;
  focus: string;
  tab: LicenseDetailTab;
  focusedActivation?: Activation;
  roles: readonly AdminRole[] | undefined;
  suggestedMaxDevices?: number;
  initialReason?: string;
  indexReturnTo: string | null;
  onDirtyChange: (dirty: boolean) => void;
  onCancel: () => void;
  onRefresh: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const identityQuery = useCurrentIdentityQuery();
  const initialIssueRestore = readIssueKeysRestore(location.state, result.license.id);
  const [issueDialogOpen, setIssueDialogOpen] = useState(Boolean(initialIssueRestore));
  const [issueDraft, setIssueDraft] = useState<IssueKeysDraft | null>(
    initialIssueRestore?.draft ?? null,
  );
  const [revokeLicenseDialogOpen, setRevokeLicenseDialogOpen] = useState(false);
  const [revokeKeyID, setRevokeKeyID] = useState<string | null>(null);
  const [revokeKeyError, setRevokeKeyError] = useState<string | null>(null);
  const [promoteLicenseDialogOpen, setPromoteLicenseDialogOpen] = useState(false);
  const [actionNotice, setActionNotice] = useState<{
    variant: "error" | "success";
    message: string;
  } | null>(null);
  const [issuedBatch, setIssuedBatch] = useState<KeyBatchResponse | null>(null);
  const [issueBusy, setIssueBusy] = useState(false);
  const issueRequestVersion = useRef(0);
  const issueIdempotency = useRef<{ intent: string; key: string } | null>(null);
  const [linkedKeyID, setLinkedKeyID] = useState<string | null>(null);
  const [replaceFlow, setReplaceFlow] = useState<{
    open: boolean;
    resume: boolean;
    licenseID: string;
    keyID: string;
    attempt: ProductKeyReplacementAttempt;
    replacement: ProductKeyReplacement | null;
    requestedKeyID: string | null;
  } | null>(null);
  const replaceProductKeyMutationKey = ["admin", "replace-product-key"] as const;
  const activationsQuery = useActivationsQuery(result.license.id, result.activations);
  const productKeysQuery = useProductKeysQuery(result.license.id);
  const productKeys = productKeysQuery.data?.pages.flatMap((page) => page.product_keys) ?? [];
  const activeKeyCount = productKeysQuery.data?.pages[0]?.active_key_count;
  const focusedProductKeyID = productKeyIDFromFocus(focus);
  const liveActivations = activationsQuery.data
    ? activationsQuery.data.pages.flatMap((page) => page.activations)
    : result.activations;
  const activations =
    focusedActivation &&
    !liveActivations.some((activation) => activation.id === focusedActivation.id)
      ? [...liveActivations, focusedActivation]
      : liveActivations;
  const timelineQuery = useLicenseTimelineQuery(result.license.id);
  const timeline = timelineQuery.data?.pages.flatMap((page) => page.events) ?? [];
  const canEditLicense = can(roles, "editLicense");
  const canIssueKeys = can(roles, "issueKeys");
  const canRevokeLicense = can(roles, "revokeLicense");
  const canDeactivateActivation = can(roles, "deactivateActivation");
  const canRevokeProductKey = can(roles, "revokeProductKey");
  const canReplaceProductKey = can(roles, "replaceProductKey");
  const canPromoteLicense = isPromotableTrial(result.license, canEditLicense);
  const needsLicenseManager = !canEditLicense || !canIssueKeys;
  const needsSecurityAdmin = !canRevokeLicense;
  const detailTabs: Array<{ id: LicenseDetailTab; label: string }> = [
    { id: "overview", label: t("search.details.tabs.overview", "Overview") },
    { id: "seats", label: t("search.details.tabs.seats", "Seats") },
    { id: "configuration", label: t("search.details.tabs.configuration", "Configuration") },
    { id: "audit", label: t("search.details.tabs.audit", "Audit") },
  ];

  function detailTabPath(nextTab: LicenseDetailTab) {
    const next = new URLSearchParams(location.search);
    if (nextTab === "overview") next.delete("tab");
    else next.set("tab", nextTab);
    if (
      (activationIDFromFocus(focus) && nextTab !== "seats") ||
      (productKeyIDFromFocus(focus) && nextTab !== "seats")
    ) next.delete("focus");
    return { pathname: location.pathname, search: next.toString() };
  }

  const replaceProductKeyMutation = useMutation({
    mutationKey: replaceProductKeyMutationKey,
    mutationFn: ({ licenseID, keyID, reason, idempotencyKey }: { licenseID: string; keyID: string; reason: string; idempotencyKey: string }) =>
      adminApi.replaceProductKey(licenseID, keyID, idempotencyKey, { reason }),
    onSuccess: async (response, variables) => {
      setReplaceFlow((current) => current?.open && current.licenseID === variables.licenseID && current.keyID === variables.keyID
        ? { ...current, replacement: response, open: true }
        : current);
      await queryClient.resetQueries({ queryKey: queryKeys.productKeys(variables.licenseID) });
      await queryClient.resetQueries({ queryKey: queryKeys.timeline(variables.licenseID) });
      await queryClient.invalidateQueries({ queryKey: ["admin", "search"] });
      await queryClient.invalidateQueries({ queryKey: ["admin", "license-index"] });
      onRefresh();
    },
  });
  const canDiscardReplacementAttempt = replaceProductKeyMutation.isError &&
    replaceProductKeyMutation.error instanceof ApiError &&
    replaceProductKeyMutation.error.status >= 400 &&
    replaceProductKeyMutation.error.status < 500;

  function clearReplaceProductKeyMutation() {
    replaceProductKeyMutation.reset();
    queryClient.getMutationCache().findAll({ mutationKey: replaceProductKeyMutationKey }).forEach((mutation) => {
      queryClient.getMutationCache().remove(mutation);
    });
  }

  function openProductKeyReplacement(keyID: string) {
    if (replaceProductKeyMutation.isPending) return;
    clearReplaceProductKeyMutation();
    const licenseID = result.license.id;
    setReplaceFlow((current) => current?.attempt.submitted
      ? { ...current, open: true, resume: false, replacement: null, requestedKeyID: current.licenseID === licenseID && current.keyID === keyID ? null : keyID }
      : current?.licenseID === licenseID && current.keyID === keyID
        ? { ...current, open: true, resume: false, replacement: null, requestedKeyID: null }
        : { open: true, resume: false, licenseID, keyID, attempt: { keyID, reason: "", idempotencyKey: createReplacementIdempotencyKey() }, replacement: null, requestedKeyID: null });
  }

  useEffect(() => () => {
    queryClient.getMutationCache().findAll({ mutationKey: replaceProductKeyMutationKey }).forEach((mutation) => {
      queryClient.getMutationCache().remove(mutation);
    });
  }, [queryClient]);

  useEffect(() => {
    const actorID = identityQuery.data?.actor_id;
    if (!actorID || replaceFlow) return;
    const recovery = readProductKeyReplacementRecovery(actorID);
    if (!recovery || recovery.licenseID !== result.license.id) return;
    setReplaceFlow({
      open: false,
      resume: true,
      licenseID: recovery.licenseID,
      keyID: recovery.keyID,
      attempt: {
        keyID: recovery.keyID,
        reason: recovery.reason,
        idempotencyKey: recovery.idempotencyKey,
        submitted: true,
      },
      replacement: null,
      requestedKeyID: null,
    });
  }, [identityQuery.data?.actor_id, replaceFlow, result.license.id]);

  useEffect(() => {
    if (!replaceFlow?.attempt.submitted || !replaceFlow.resume || replaceFlow.open) return;
    if (replaceFlow.licenseID !== result.license.id) return;
    setReplaceFlow((current) => current ? { ...current, open: true, resume: false } : current);
  }, [replaceFlow?.attempt.submitted, replaceFlow?.licenseID, replaceFlow?.open, replaceFlow?.resume, result.license.id]);

  useEffect(() => () => {
    issueRequestVersion.current += 1;
  }, []);

  useEffect(() => {
    issueRequestVersion.current += 1;
    issueIdempotency.current = null;
    setIssuedBatch(null);
    setIssueBusy(false);
    const restored = readIssueKeysRestore(location.state, result.license.id);
    setIssueDraft(restored?.draft ?? null);
    setIssueDialogOpen(Boolean(restored));
  }, [location.key, location.state, result.license.id]);

  useEffect(() => {
    if (
      !focusedProductKeyID ||
      productKeys.some((key) => key.id === focusedProductKeyID) ||
      productKeysQuery.isPending ||
      productKeysQuery.isFetchingNextPage ||
      !productKeysQuery.hasNextPage
    ) return;
    void productKeysQuery.fetchNextPage();
  }, [
    focusedProductKeyID,
    productKeys,
    productKeysQuery.fetchNextPage,
    productKeysQuery.hasNextPage,
    productKeysQuery.isFetchingNextPage,
    productKeysQuery.isPending,
  ]);

  function invalidateIndexes() {
    return queryClient.invalidateQueries({ queryKey: ["admin", "license-index"] });
  }

  const revokeMutation = useMutation({
    mutationFn: (reason: string) => adminApi.revokeLicense(result.license.id, reason),
    onSuccess: async () => {
      setRevokeLicenseDialogOpen(false);
      setActionNotice({
        variant: "success",
        message: t("search.details.licenseRevokedNotice", "License revoked. New token issuance is now blocked by the service."),
      });
      await queryClient.invalidateQueries({ queryKey: ["admin", "search"] });
      await invalidateIndexes();
      await queryClient.resetQueries({ queryKey: queryKeys.timeline(result.license.id) });
      await queryClient.resetQueries({ queryKey: queryKeys.productKeys(result.license.id) });
      onRefresh();
    },
    onError: (error) => setActionNotice({ variant: "error", message: apiErrorMessage(error) }),
  });

  const promoteMutation = useMutation({
    mutationFn: ({ body, idempotencyKey }: { body: PromoteLicensePayload; idempotencyKey: string }) =>
      adminApi.promoteLicense(result.license.id, body, idempotencyKey),
    onSuccess: async () => {
      setPromoteLicenseDialogOpen(false);
      setActionNotice({
        variant: "success",
        message: t("search.details.licensePromotedNotice", "Trial License promoted to Paid. License ID, Product Key, and existing Activations are unchanged; clients refresh for the Paid token."),
      });
      await queryClient.invalidateQueries({ queryKey: ["admin", "search"] });
      await invalidateIndexes();
      await queryClient.resetQueries({ queryKey: queryKeys.timeline(result.license.id) });
      onRefresh();
    },
    onError: (error) => setActionNotice({ variant: "error", message: apiErrorMessage(error) }),
  });

  async function issueKeys({ count, reason }: { count: number; reason: string }) {
    const requestVersion = ++issueRequestVersion.current;
    const intent = JSON.stringify({ count, reason });
    if (issueIdempotency.current?.intent !== intent) {
      issueIdempotency.current = { intent, key: crypto.randomUUID() };
    }
    setIssueBusy(true);
    try {
      const response = await adminApi.issueKeys(result.license.id, count, reason, issueIdempotency.current.key);
      if (requestVersion !== issueRequestVersion.current) return;
      setIssuedBatch(response);
      setActionNotice({
        variant: "success",
        message: t("search.details.keysIssuedNotice", "{{count}} Product Key issued. Copy and store it now; the service returns full keys only at delivery.", { count: response.keys.length }),
      });
      await queryClient.invalidateQueries({ queryKey: ["admin", "search"] });
      await invalidateIndexes();
      await queryClient.resetQueries({ queryKey: queryKeys.timeline(result.license.id) });
      await queryClient.resetQueries({ queryKey: queryKeys.productKeys(result.license.id) });
    } catch (error) {
      if (requestVersion === issueRequestVersion.current) {
        setActionNotice({ variant: "error", message: apiErrorMessage(error) });
      }
    } finally {
      if (requestVersion === issueRequestVersion.current) setIssueBusy(false);
    }
  }

  const deactivateActivationMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminApi.adminDeactivateActivation(id, reason),
    onSuccess: async (_response, variables) => {
      setActionNotice({
        variant: "success",
        message: t("search.details.activationDeactivatedNotice", "Device {{id}} is deactivated. Capacity has been refreshed.", { id: variables.id }),
      });
      await queryClient.resetQueries({ queryKey: queryKeys.activations(result.license.id) });
      await queryClient.resetQueries({ queryKey: queryKeys.timeline(result.license.id) });
      await queryClient.invalidateQueries({ queryKey: ["admin", "search"] });
      await invalidateIndexes();
      onRefresh();
    },
  });

  const revokeProductKeyMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminApi.revokeProductKey(id, reason),
    onSuccess: async (_response, variables) => {
      setRevokeKeyID(null);
      setRevokeKeyError(null);
      setActionNotice({
        variant: "success",
        message: t("search.details.productKeyRevokedNotice", "Product Key {{id}} revoked. Its current Device is deactivated.", { id: variables.id }),
      });
      await queryClient.resetQueries({ queryKey: queryKeys.productKeys(result.license.id) });
      await queryClient.resetQueries({ queryKey: queryKeys.activations(result.license.id) });
      await queryClient.resetQueries({ queryKey: queryKeys.timeline(result.license.id) });
      await queryClient.invalidateQueries({ queryKey: ["admin", "search"] });
      await invalidateIndexes();
      onRefresh();
    },
    onError: (error) => setRevokeKeyError(apiErrorMessage(error)),
  });

  return (
    <div className="flex flex-col gap-4">
      <PageHeader className="gap-0 pb-0 sm:block">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <ArrowLeft className="size-4" aria-hidden="true" />
            <span className="sr-only">{t("common.back", "Back to licenses")}</span>
          </Button>
          <div className="min-w-0 flex-1 basis-[200px]">
            <h1 className="wrap-break-word text-2xl font-medium tracking-[-0.04em] text-ink">
              {result.license.alias?.trim() || result.customer.name}
            </h1>
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
              <span className="wrap-break-word wrap-anywhere">{result.license.alias?.trim() ? `${result.customer.name} · ${result.product.name}` : result.product.name}</span>
              <span aria-hidden="true">·</span>
              <span>{result.license.kind === "trial" ? t("licenses.trial", "Trial") : t("licenses.paid", "Paid")}</span>
              <span aria-hidden="true">·</span>
              <span className="flex min-w-0 items-center gap-1 font-mono">
                <code className="truncate" title={result.license.id}>{result.license.id}</code>
                <CopyIdButton value={result.license.id} label={t("nav.licenses", "License ID")} />
              </span>
            </div>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <LicenseStatusBadge
              status={result.license.status}
              validUntil={result.license.valid_until}
            />
            {canPromoteLicense && (
              <Button variant="outline" size="sm" onClick={() => setPromoteLicenseDialogOpen(true)}>
                <TrendingUp className="size-3.5" aria-hidden="true" />
                {t("search.details.promoteLicense", "Promote to Paid")}
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              disabled={!canIssueKeys}
              onClick={() => {
                setIssuedBatch(null);
                setIssueDraft(null);
                setIssueDialogOpen(true);
              }}
            >
              <KeyRound className="size-3.5" aria-hidden="true" />
              {t("search.details.issueKeys", "Issue keys")}
            </Button>
            <Menu.Root>
              <Menu.Trigger render={<Button variant="outline" size="icon" aria-label={t("common.actions", "Actions")} />}>
                <Ellipsis className="size-4" aria-hidden="true" />
              </Menu.Trigger>
              <Menu.Portal>
                <Menu.Positioner align="end" sideOffset={6} className="z-50">
                  <Menu.Popup className="min-w-48 rounded-lg border border-line bg-panel p-1 text-sm shadow-panel outline-hidden">
                    <Menu.Item
                      className="flex min-h-9 cursor-default items-center gap-2 rounded-md px-3 text-danger outline-hidden data-highlighted:bg-danger/10 data-disabled:opacity-45"
                      disabled={!canRevokeLicense}
                      onClick={() => setRevokeLicenseDialogOpen(true)}
                    >
                      <Ban className="size-3.5" aria-hidden="true" />
                      {t("search.details.revokeLicense", "Revoke license")}
                    </Menu.Item>
                  </Menu.Popup>
                </Menu.Positioner>
              </Menu.Portal>
            </Menu.Root>
          </div>
        </div>
        <nav
          className="mt-2 flex shrink-0 gap-6 overflow-x-auto"
          aria-label={t("search.details.tabs.aria", "License detail sections")}
        >
          {detailTabs.map((detailTab) => (
            <Link
              key={detailTab.id}
              to={detailTabPath(detailTab.id)}
              state={location.state}
              className={cn(
                "shrink-0 border-b-2 border-transparent px-0 py-3 text-xs font-medium text-muted transition-colors hover:text-ink focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus",
                tab === detailTab.id && "border-ink text-ink",
              )}
              aria-current={tab === detailTab.id ? "page" : undefined}
            >
              {detailTab.label}
            </Link>
          ))}
        </nav>
      </PageHeader>
      {(needsLicenseManager || needsSecurityAdmin) && (
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {needsLicenseManager && <PermissionHint role={requiredRole("editLicense")} />}
          {needsSecurityAdmin && <PermissionHint role={requiredRole("revokeLicense")} />}
        </div>
      )}
      {actionNotice && (
        <InlineAlert
          title={
            actionNotice.variant === "success"
              ? t("search.details.actionRecorded", "Action recorded")
              : t("search.details.actionBlocked", "Action blocked")
          }
          message={actionNotice.message}
          variant={actionNotice.variant}
        />
      )}

      <div hidden={tab !== "overview"}>
        <OverviewPanel
          result={result}
          activations={activations}
          seatLimit={result.license.policy.max_devices}
          allocated={activeKeyCount ?? productKeys.filter((key) => key.status === "active").length}
          inUse={Math.max(
            result.active_activation_count,
            activations.filter((activation) => activation.status === "active").length,
          )}
          events={timeline}
          timelineLoading={timelineQuery.isPending}
          timelineError={timelineQuery.error}
          canEditLicense={canEditLicense}
          seatsHref={detailTabPath("seats")}
          configurationHref={detailTabPath("configuration")}
          auditHref={detailTabPath("audit")}
        />
      </div>
      <div hidden={tab !== "seats"}>
        <SeatsPanel
          keys={productKeys}
          activations={activations}
          seatLimit={result.license.policy.max_devices}
          activeKeyCount={activeKeyCount}
          activeActivationCount={result.active_activation_count}
          loading={productKeysQuery.isPending}
          loadingMore={productKeysQuery.isFetchingNextPage}
          hasNextPage={productKeysQuery.hasNextPage}
          error={productKeysQuery.error}
          focusedKeyID={focusedProductKeyID}
          canRevoke={canRevokeProductKey}
          canReplace={canReplaceProductKey}
          busyKeyID={revokeProductKeyMutation.isPending ? revokeProductKeyMutation.variables?.id : replaceProductKeyMutation.isPending ? replaceProductKeyMutation.variables?.keyID : undefined}
          onOpenRevoke={(id) => {
            revokeProductKeyMutation.reset();
            setRevokeKeyError(null);
            setRevokeKeyID(id);
          }}
          onOpenReplace={openProductKeyReplacement}
          onViewLinked={(id) => setLinkedKeyID(id)}
          onRetry={() => void productKeysQuery.refetch()}
          onLoadMore={() => void productKeysQuery.fetchNextPage()}
        />
      </div>
      <div hidden={tab !== "configuration"}>
        <EditLicenseFormContainer
          key={`${result.license.id}:${formRevision}`}
          result={result}
          allowed={canEditLicense}
          savedAt={savedAt}
          notifyOnSave={notifyOnSave}
          overviewHref={detailTabPath("overview")}
          overviewState={location.state}
          suggestedMaxDevices={suggestedMaxDevices}
          initialReason={initialReason}
          onCancel={onCancel}
          onDirtyChange={onDirtyChange}
          onSuccess={onSaved}
        />
      </div>
      <div hidden={tab !== "audit"}>
        <Timeline
          events={timeline}
          loading={timelineQuery.isPending}
          loadingMore={timelineQuery.isFetchingNextPage}
          hasNextPage={timelineQuery.hasNextPage}
          error={timelineQuery.error}
          onLoadMore={() => void timelineQuery.fetchNextPage()}
        />
      </div>

      <IssueKeysDialog
        open={issueDialogOpen}
        onOpenChange={(open) => {
          setIssueDialogOpen(open);
          if (!open) {
            issueIdempotency.current = null;
            setIssueDraft(null);
            if (readIssueKeysRestore(location.state, result.license.id)) {
              navigate(
                { pathname: location.pathname, search: location.search },
                { replace: true, state: indexReturnTo ? { indexReturnTo } : null },
              );
            }
          }
        }}
        licenseId={result.license.id}
        busy={issueBusy}
        batch={issuedBatch}
        result={result}
        capacity={{
          current: activeKeyCount ?? productKeys.filter((key) => key.status === "active").length,
          limit: result.license.policy.max_devices,
        }}
        canIncreaseDeviceLimit={canEditLicense}
        initialDraft={issueDraft}
        onClearBatch={() => setIssuedBatch(null)}
        onSubmit={issueKeys}
        onIncreaseDeviceLimit={({ count, reason, suggestedLimit }) => {
          navigate(
            { pathname: adminLicensePath(result.license.id), search: "?tab=configuration" },
            {
              state: {
                kind: "increase-device-limit" as const,
                licenseId: result.license.id,
                returnTo: `${location.pathname}${location.search}`,
                suggestedLimit,
                draft: { count, reason },
                indexReturnTo,
              },
            },
          );
        }}
      />
      <LinkedDevicesDialog
        licenseId={result.license.id}
        productKeyId={linkedKeyID}
        canDeactivate={canDeactivateActivation}
        onDeactivate={async (id, reason) => {
          await deactivateActivationMutation.mutateAsync({ id, reason });
        }}
        onOpenChange={(open) => {
          if (!open) setLinkedKeyID(null);
        }}
      />
      <RevokeLicenseDialog
        open={revokeLicenseDialogOpen}
        onOpenChange={setRevokeLicenseDialogOpen}
        licenseId={result.license.id}
        activationCount={result.activation_count}
        busy={revokeMutation.isPending}
        onRevoke={(reason) => revokeMutation.mutate(reason)}
      />
      <RevokeProductKeyDialog
        open={Boolean(revokeKeyID)}
        onOpenChange={(open) => {
          if (!open) {
            setRevokeKeyID(null);
            setRevokeKeyError(null);
            revokeProductKeyMutation.reset();
          }
        }}
        keyID={revokeKeyID ?? ""}
        busy={revokeProductKeyMutation.isPending}
        error={revokeKeyError}
        onRevoke={(reason) => revokeKeyID && revokeProductKeyMutation.mutate({ id: revokeKeyID, reason })}
      />
      <PromoteLicenseDialog
        open={promoteLicenseDialogOpen}
        onOpenChange={setPromoteLicenseDialogOpen}
        license={result.license}
        product={result.product}
        busy={promoteMutation.isPending}
        error={promoteMutation.isError ? apiErrorMessage(promoteMutation.error) : null}
        onPromote={(body, idempotencyKey) => promoteMutation.mutate({ body, idempotencyKey })}
      />
      <ReplaceProductKeyDialog
        open={Boolean(replaceFlow?.open)}
        onOpenChange={(open) => {
          if (!open) setReplaceFlow((current) => current ? { ...current, open: false, resume: false } : current);
        }}
        keyID={replaceFlow?.keyID ?? ""}
        busy={replaceProductKeyMutation.isPending}
        error={replaceProductKeyMutation.isError ? apiErrorMessage(replaceProductKeyMutation.error) : null}
        canDiscard={canDiscardReplacementAttempt}
        requestedKeyID={replaceFlow?.requestedKeyID}
        replacement={replaceFlow?.replacement ?? null}
        attempt={replaceFlow?.attempt ?? null}
        onAttemptChange={(attempt) => setReplaceFlow((current) => current ? { ...current, attempt } : current)}
        onReplace={(reason, idempotencyKey) => {
          if (!replaceFlow) return;
          const attempt = { ...replaceFlow.attempt, reason, idempotencyKey, submitted: true };
          const actorID = identityQuery.data?.actor_id;
          if (actorID) {
            writeProductKeyReplacementRecovery(actorID, {
              licenseID: replaceFlow.licenseID,
              keyID: replaceFlow.keyID,
              reason,
              idempotencyKey,
            });
          }
          setReplaceFlow((current) => current ? { ...current, attempt } : current);
          replaceProductKeyMutation.mutate({ licenseID: replaceFlow.licenseID, keyID: replaceFlow.keyID, reason, idempotencyKey });
        }}
        onFinish={() => {
          const actorID = identityQuery.data?.actor_id;
          if (actorID) clearProductKeyReplacementRecovery(actorID);
          setReplaceFlow(null);
          clearReplaceProductKeyMutation();
        }}
        onDiscard={() => {
          const actorID = identityQuery.data?.actor_id;
          if (actorID) clearProductKeyReplacementRecovery(actorID);
          setReplaceFlow(null);
          clearReplaceProductKeyMutation();
        }}
        onResolveTargetMismatch={() => setReplaceFlow((current) => current ? { ...current, requestedKeyID: null } : current)}
      />
    </div>
  );
}


function DirtyNavigationGuard({
  dirty,
  onBlocked,
}: {
  dirty: boolean;
  onBlocked: (blocked: { proceed: () => void; reset: () => void } | null) => void;
}) {
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  if (!dataRouter) return null;
  return <DataRouterDirtyNavigationGuard dirty={dirty} onBlocked={onBlocked} />;
}

function DataRouterDirtyNavigationGuard({
  dirty,
  onBlocked,
}: {
  dirty: boolean;
  onBlocked: (blocked: { proceed: () => void; reset: () => void } | null) => void;
}) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && (currentLocation.pathname !== nextLocation.pathname
        || licenseDetailTab(new URLSearchParams(currentLocation.search).get("tab"), new URLSearchParams(currentLocation.search).get("focus") ?? "")
          !== licenseDetailTab(new URLSearchParams(nextLocation.search).get("tab"), new URLSearchParams(nextLocation.search).get("focus") ?? "")),
  );
  const reportedBlocked = useRef(false);
  useEffect(() => {
    if (blocker.state === "blocked") {
      if (reportedBlocked.current) return;
      reportedBlocked.current = true;
      onBlocked({ proceed: blocker.proceed, reset: blocker.reset });
      return;
    }
    reportedBlocked.current = false;
    onBlocked(null);
  }, [blocker.proceed, blocker.reset, blocker.state, onBlocked]);
  return null;
}

function readIndexReturnTo(state: unknown): string | null {
  if (!state || typeof state !== "object") return null;
  const value = (state as { indexReturnTo?: unknown }).indexReturnTo;
  if (typeof value !== "string") return null;
  const url = new URL(value, "https://admin.local");
  if (
    url.origin !== "https://admin.local" ||
    (url.pathname !== "/admin/licenses" && url.pathname !== "/admin/customers")
  ) return null;
  if (url.pathname === "/admin/licenses") {
    url.searchParams.delete("create");
    url.searchParams.delete("license");
    url.searchParams.delete("focus");
    url.searchParams.delete("tab");
  }
  const search = url.searchParams.toString();
  return search ? `${url.pathname}?${search}` : url.pathname;
}

function displayDeviceName(computerName: string, installationID: string, unknownDevice: string) {
  return computerName === installationID ? unknownDevice : computerName;
}

function LinkedDevicesDialog({
  licenseId,
  productKeyId,
  canDeactivate,
  onDeactivate,
  onOpenChange,
}: {
  licenseId: string;
  productKeyId: string | null;
  canDeactivate: boolean;
  onDeactivate: (id: string, reason: string) => Promise<void>;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();
  const unknownDevice = t("devices.unknownDevice", "Unknown device");
  const query = useProductKeyActivationsQuery(licenseId, productKeyId);
  const [activeDeactivateId, setActiveDeactivateId] = useState<string | null>(null);
  const [deactivateBusy, setDeactivateBusy] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const activations = query.data?.pages.flatMap((page) => page.activations) ?? [];
  const deactivateTarget = activations.find((activation) => activation.id === activeDeactivateId);
  async function deactivateLinkedActivation(id: string, reason: string) {
    if (deactivateBusy) return;
    setDeactivateBusy(true);
    setDeactivateError(null);
    try {
      await onDeactivate(id, reason);
      await query.refetch();
      setActiveDeactivateId(null);
    } catch (error) {
      setDeactivateError(apiErrorMessage(error));
    } finally {
      setDeactivateBusy(false);
    }
  }
  return <Dialog open={Boolean(productKeyId)} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-xl">
      <DialogHeader>
        <DialogTitle>{t("search.details.linkedDevicesTitle", "Linked devices")}</DialogTitle>
        <DialogDescription>{t("search.details.linkedDevicesDescription", "Activations created with this Product Key. Loaded records are not a total.")}</DialogDescription>
      </DialogHeader>
      {query.isPending ? <div className="h-14 rounded-md bg-panel-raised" role="status" aria-label={t("search.details.loadingActivations", "Loading Activations")} /> : query.error ? <InlineAlert title={t("search.details.linkedDevicesUnavailable", "Linked devices could not load.")} message={apiErrorMessage(query.error)} onRetry={() => void query.refetch()} /> : activations.length === 0 ? <p className="text-sm text-muted">{t("search.details.noLinkedDevices", "No linked Activations found for this Product Key.")}</p> : <div className="space-y-2">{activations.map((activation) => <div className="rounded-md border border-line bg-canvas p-3" key={activation.id}><div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="wrap-anywhere text-sm font-medium text-ink">{displayDeviceName(activation.computer_name, activation.installation_id, unknownDevice)}</p><p className="mt-1 font-mono text-xs text-muted">{activation.id}</p><p className="mt-1 wrap-anywhere text-xs text-muted">{activation.installation_id} · {activation.platform} · {activation.app_version}</p><p className="mt-1 text-xs text-muted">{t("search.details.lastSeen", "Last seen")} {formatDateTime(activation.last_seen_at)}</p></div><Button variant="ghost" size="sm" disabled={activation.status !== "active" || !canDeactivate} onClick={() => setActiveDeactivateId(activation.id)}>{t("dialogs.confirmDeactivate", "Deactivate Activation")}</Button></div></div>)}{query.hasNextPage && <Button className="w-full" variant="outline" size="sm" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>{query.isFetchingNextPage ? t("common.loading", "Loading…") : t("search.details.loadMoreActivations", "Load more Activations")}</Button>}</div>}
      <DialogFooter><Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>{t("common.close", "Close")}</Button></DialogFooter>
      <DeactivateActivationDialog open={Boolean(deactivateTarget)} onOpenChange={(open) => !open && setActiveDeactivateId(null)} target={{ activationId: deactivateTarget?.id ?? "", licenseId, computerName: deactivateTarget ? displayDeviceName(deactivateTarget.computer_name, deactivateTarget.installation_id, unknownDevice) : "", location: deactivateTarget ? activationLocation(deactivateTarget) : null }} busy={deactivateBusy} error={deactivateError} onDeactivate={(id, reason) => void deactivateLinkedActivation(id, reason)} />
    </DialogContent>
  </Dialog>;
}

function OverviewPanel({
  result,
  activations,
  seatLimit,
  allocated,
  inUse,
  events,
  timelineLoading,
  timelineError,
  canEditLicense,
  seatsHref,
  configurationHref,
  auditHref,
}: {
  result: SearchResult;
  activations: Activation[];
  seatLimit: number;
  allocated: number;
  inUse: number;
  events: AuditEvent[];
  timelineLoading: boolean;
  timelineError: unknown;
  canEditLicense: boolean;
  seatsHref: { pathname: string; search: string };
  configurationHref: { pathname: string; search: string };
  auditHref: { pathname: string; search: string };
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { formatDateTime, resolvedTimezone } = useTimezone();
  const lastSeenAt = activations.reduce<string | null>((latest, activation) => {
    if (!latest || Date.parse(activation.last_seen_at) > Date.parse(latest)) return activation.last_seen_at;
    return latest;
  }, null);
  const modelLabel =
    result.license.model === "time_limited"
      ? t("licenses.models.timeLimited", "Time limited")
      : result.license.model === "perpetual"
        ? t("licenses.models.perpetual", "Perpetual")
        : t("licenses.models.nodeLocked", "Node locked");
  const kindLabel = result.license.kind === "trial" ? t("licenses.trial", "Trial") : t("licenses.paid", "Paid");
  const seats = licenseSeatSnapshot(seatLimit, allocated, inUse);
  const validity = licenseValiditySnapshot(result.license.valid_from, result.license.valid_until, Date.now(), resolvedTimezone);
  const seatRiskText =
    seats.risk === "at"
      ? t("search.details.atSeatCapacity", "At seat capacity")
      : seats.risk === "near"
        ? t("search.details.nearSeatCapacity", "Seat capacity nearly full")
        : t("search.details.seatCapacityHealthy", "Seat capacity has room");
  let validityRiskText = t("search.details.validityUnknown");
  if (validity.kind !== "unknown") {
    if (validity.kind === "finite" && validity.risk === "expired") {
      const days = Math.abs(validity.calendarDaysRemaining);
      validityRiskText = days === 0
        ? t("search.details.expiredToday")
        : t("search.details.expiredDaysAgo", { count: days });
    } else if (validity.startsInDays !== null) {
      validityRiskText = validity.startsInDays === 0
        ? t("search.details.startsToday")
        : t("search.details.startsInDays", { count: validity.startsInDays });
    } else if (validity.kind === "perpetual") {
      validityRiskText = t("search.details.noExpiry");
    } else {
      validityRiskText = validity.calendarDaysRemaining === 0
        ? t("search.details.endsToday")
        : validity.calendarDaysRemaining === 1
          ? t("search.details.endsTomorrow")
          : t("search.details.remainingDays", { count: validity.calendarDaysRemaining });
    }
  }
  const recentEvents = [...events]
    .sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime())
    .slice(0, 5);
  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-lg border border-line bg-panel px-5 py-4" aria-labelledby="overview-status-heading">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 id="overview-status-heading" className="font-semibold text-ink">
              {t("search.details.overviewStatus", "License status")}
            </h3>
            <p className="mt-0.5 text-xs text-muted">
              {t("search.details.overviewHint", "Read-only snapshot of this License. Manage Product Keys on Seats. Change alias, validity, Entitlements, or Policy on Configuration.")}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              className="text-xs font-medium text-accent underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus"
              to={seatsHref}
              state={location.state}
            >
              {t("search.details.openSeats", "Open Seats")}
            </Link>
            <Link
              className="text-xs font-medium text-accent underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus"
              to={configurationHref}
              state={location.state}
            >
              {t("search.details.openConfiguration", "Open Configuration")}
            </Link>
          </div>
        </div>
        <div className="mt-5 grid gap-6 lg:grid-cols-2">
          <div data-seat-risk={seats.risk}>
            <h4 className="text-sm font-medium text-ink">{t("search.details.overviewSeats", "Seat use")}</h4>
            <p className="mt-1 font-mono text-sm tabular-nums text-ink">
              {t("search.details.inUseOfLimit", "{{used}} of {{limit}} in use", { used: inUse, limit: seats.limit })}
              <span className="text-muted">
                {" · "}
                {t("search.details.allocated", "Allocated")} {seats.issued}
              </span>
            </p>
            <div
              className="mt-2 flex h-2 overflow-hidden rounded-full bg-panel-raised"
              role="img"
              aria-label={t("search.details.seatBarAria", "{{used}} in use, {{issued}} allocated, {{idle}} allocated idle, {{unallocated}} unallocated, limit {{limit}}", {
                used: inUse,
                issued: seats.issued,
                idle: seats.allocatedIdle,
                unallocated: seats.unallocated,
                limit: seats.limit,
              })}
            >
              {seats.usedWidth > 0 ? <span className="bg-ink" style={{ width: `${seats.usedWidth}%` }} /> : null}
              {seats.idleWidth > 0 ? <span className="bg-muted/55" style={{ width: `${seats.idleWidth}%` }} /> : null}
            </div>
            <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs tabular-nums text-muted">
              <li className="flex items-center gap-1.5">
                <span className="size-1.5 rounded-full bg-ink" aria-hidden="true" />
                {t("search.details.inUse", "In use")} {inUse}
              </li>
              <li className="flex items-center gap-1.5">
                <span className="size-1.5 rounded-full bg-muted/55" aria-hidden="true" />
                {t("search.details.allocatedIdle", "Allocated, idle")} {seats.allocatedIdle}
              </li>
              <li className="flex items-center gap-1.5">
                <span className="size-1.5 rounded-full bg-panel-raised ring-1 ring-line" aria-hidden="true" />
                {t("search.details.unallocated", "Unallocated")} {seats.unallocated}
              </li>
              <li>
                {t("search.details.seatLimit", "Seat limit")} {seats.limit}
              </li>
            </ul>
            <p
              className={cn(
                "mt-2 text-[11px]",
                seats.risk === "at" ? "text-danger" : seats.risk === "near" ? "text-warning" : "text-muted",
              )}
            >
              {seats.overAllocated > 0
                ? t("search.details.overAllocated", "{{count}} allocated over the Seat limit", { count: seats.overAllocated })
                : seatRiskText}
            </p>
          </div>
          <div data-validity-risk={validity.kind === "finite" ? validity.risk : validity.kind}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-medium text-ink">{t("search.details.overviewValidity", "Validity")}</h4>
              {canEditLicense && (
                <Link
                  className="text-xs font-medium text-focus underline-offset-4 hover:text-focus/80 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus"
                  to={configurationHref}
                  state={location.state}
                >
                  {t("search.details.editValidity")}
                </Link>
              )}
            </div>
            <p
              className={cn(
                "mt-2 flex items-center gap-2 text-xl font-medium tracking-tight sm:text-2xl",
                validity.kind === "finite" && validity.risk === "expired"
                  ? "text-danger"
                  : validity.kind === "finite" && validity.startsInDays === null && validity.risk === "soon"
                    ? "text-warning"
                    : "text-ink",
              )}
            >
              <Clock3 className="size-5 shrink-0" aria-hidden="true" />
              {validityRiskText}
            </p>
            <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-xs">
              <dt className="text-muted">{t("search.details.validFrom")}</dt>
              <dd className="min-w-0 text-ink">{formatDateTime(result.license.valid_from)}</dd>
              <dt className="text-muted">{t("licenses.validUntil", "Valid until")}</dt>
              <dd className="min-w-0 text-ink">
                {result.license.valid_until
                  ? formatDateTime(result.license.valid_until)
                  : t("search.details.noExpiry")}
              </dd>
            </dl>
          </div>
        </div>
        <dl className="mt-6 grid gap-3 border-t border-line pt-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-[11px] text-muted">{t("search.details.lifecycle", "Lifecycle")}</dt>
            <dd className="mt-1"><LicenseStatusBadge status={result.license.status} /></dd>
          </div>
          <div>
            <dt className="text-[11px] text-muted">{t("licenses.kind", "Kind")}</dt>
            <dd className="mt-1 text-sm text-ink">{kindLabel} · {modelLabel}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-muted">{t("licenses.customer", "Customer")}</dt>
            <dd className="mt-1 text-sm text-ink">{result.customer.name} · {result.product.name}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-muted">{t("search.details.lastSeen", "Last seen")}</dt>
            <dd className="mt-1 text-sm text-ink">
              {lastSeenAt ? formatDateTime(lastSeenAt) : t("search.details.noRecentActivity", "No Device has checked in yet.")}
            </dd>
          </div>
        </dl>
      </section>
      <section className="rounded-lg border border-line bg-panel px-5 py-4" aria-labelledby="overview-audit-heading">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 id="overview-audit-heading" className="font-semibold text-ink">
              {t("search.details.recentAudit", "Recent audit")}
            </h3>
            <p className="mt-0.5 text-xs text-muted">
              {t("search.details.recentAuditHint", "Newest events for this License. The full timeline is on Audit.")}
            </p>
          </div>
          <Link
            className="text-xs font-medium text-accent underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus"
            to={auditHref}
            state={location.state}
          >
            {t("search.details.openAudit", "Open Audit")}
          </Link>
        </div>
        {Boolean(timelineError) && (
          <p className="mt-3 text-xs text-warning">
            {t("search.details.timelineUnavailable", "Audit timeline is temporarily unavailable.")}
          </p>
        )}
        {timelineLoading ? (
          <div
            className="mt-4 h-14 rounded-md bg-panel-raised"
            aria-label={t("search.details.timelineLoading", "Loading Audit timeline")}
            role="status"
          />
        ) : recentEvents.length === 0 ? (
          <p className="mt-4 text-sm text-muted">
            {t("search.details.timelineEmpty", "No Audit events were returned for this record.")}
          </p>
        ) : (
          <div className="mt-4 divide-y divide-line rounded-md border border-line bg-canvas">
            {recentEvents.map((event) => (
              <article className="p-3 text-xs" key={event.id}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-ink">{humanizeAction(event.action)}</span>
                  <time className="font-mono text-xs text-muted" dateTime={event.created_at}>
                    {formatDateTime(event.created_at)}
                  </time>
                </div>
                {event.reason ? <p className="mt-1 text-ink/90">{t("search.details.reason", "Reason")}: {event.reason}</p> : null}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function SeatsPanel({
  keys,
  activations,
  seatLimit,
	activeKeyCount,
  activeActivationCount,
  loading,
  loadingMore,
  hasNextPage,
  error,
  focusedKeyID,
  canRevoke,
  canReplace,
  busyKeyID,
  onOpenRevoke,
  onOpenReplace,
  onViewLinked,
  onRetry,
  onLoadMore,
}: {
  keys: ProductKeyMetadata[];
  activations: Activation[];
  seatLimit: number;
  activeKeyCount?: number;
  activeActivationCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasNextPage: boolean;
  error: unknown;
  focusedKeyID?: string;
  canRevoke: boolean;
  canReplace: boolean;
  busyKeyID?: string;
  onOpenRevoke: (id: string) => void;
  onOpenReplace: (id: string) => void;
  onViewLinked: (id: string) => void;
  onRetry: () => void;
  onLoadMore: () => void;
}) {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();
  const unknownDevice = t("devices.unknownDevice", "Unknown device");
  const usableKeys = keys.filter((key) => key.status === "active");
	const activeActivationByKey = new Map(
		keys
			.filter((key) => key.current_activation)
			.map((key) => [key.id, key.current_activation!]),
	);
	for (const activation of activations) {
		if (activation.status === "active" && activation.source_product_key_id && !activeActivationByKey.has(activation.source_product_key_id)) {
			activeActivationByKey.set(activation.source_product_key_id, activation);
		}
	}
  const allocated = activeKeyCount ?? usableKeys.length;
  const inUse = activeActivationByKey.size;
  const unallocated = Math.max(0, seatLimit - allocated);
  return (
    <section className="rounded-lg border border-line bg-panel px-5 py-4" aria-labelledby="seats-heading">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 id="seats-heading" className="font-semibold text-ink">
            {t("search.details.seats", "Seats")}
          </h3>
          <p className="mt-0.5 text-xs text-muted">
            {t("search.details.seatsHint", "One usable Product Key is one allocated seat. Full Product Keys are never shown here.")}
          </p>
        </div>
        <Badge variant="neutral">{t("search.details.allocatedSeats", "{{count}} allocated", { count: allocated })}</Badge>
      </div>
      <dl className="mt-4 grid grid-cols-2 divide-x divide-y divide-line overflow-hidden rounded-md border border-line sm:grid-cols-4 sm:divide-y-0">
        {[
          [t("search.details.seatLimit", "Seat limit"), seatLimit],
          [t("search.details.allocated", "Allocated"), allocated],
          [t("search.details.inUse", "In use"), Math.max(activeActivationCount, inUse)],
          [t("search.details.unallocated", "Unallocated"), unallocated],
        ].map(([label, value]) => <div className="p-3" key={String(label)}><dt className="text-[11px] text-muted">{label}</dt><dd className="mt-1 font-mono text-lg text-ink">{value}</dd></div>)}
      </dl>
      {loading ? (
        <div className="mt-4 h-14 rounded-md bg-panel-raised" role="status" aria-label={t("search.details.loadingProductKeys", "Loading Product Keys")} />
      ) : error ? (
        <div className="mt-3">
          <InlineAlert
            title={t("search.details.productKeysUnavailable", "Product Key list is unavailable.")}
            message={t("search.details.productKeysUnavailableHelp", "Other License details remain available. Retry this panel.")}
            onRetry={onRetry}
          />
        </div>
      ) : usableKeys.length === 0 ? (
        <p className="mt-4 text-sm text-muted">{t("search.details.noSeats", "No allocated seats yet. Issue Product Keys to allocate seats.")}</p>
      ) : (
        <div className="mt-4 overflow-x-auto border border-line">
          <table className="w-full min-w-[760px] text-left text-xs">
            <thead className="border-b border-line bg-panel-raised text-muted"><tr><th className="px-3 py-2 font-medium">{t("search.details.productKey", "Product Key")}</th><th className="px-3 py-2 font-medium">{t("search.details.currentDevice", "Current Device")}</th><th className="px-3 py-2 font-medium">{t("common.status", "Status")}</th><th className="px-3 py-2 font-medium">{t("common.actions", "Actions")}</th></tr></thead>
            <tbody>
          {usableKeys.map((key) => {
            const focused = key.id === focusedKeyID;
            const activation = activeActivationByKey.get(key.id);
            return (
            <tr className={cn("border-b border-line last:border-0", focused && "bg-accent/5")} key={key.id}>
              <td className="px-3 py-3 align-top">
                <div className="flex items-center gap-2">
                  <KeyRound className="size-3.5 text-muted" aria-hidden="true" />
                  <span className="font-mono text-xs text-ink">{formatMaskedProductKey(key.masked_prefix, key.masked_suffix)}</span>
                  <Badge variant={activation ? "success" : "neutral"}>{activation ? t("search.details.inUse", "In use") : t("search.details.available", "Available")}</Badge>
                </div>
                <p className="mt-2 font-mono text-[11px] text-muted">{key.id} · {key.batch_id}</p>
                <p className="mt-1 text-xs text-muted">
                  {t("search.details.issuedAt", "Issued")} {formatDateTime(key.created_at)}
                </p>
                {focused && (
                  <p className="mt-2 text-xs text-accent">
                    {t("search.details.productKeyFocusHint", "Selected from the global Product Keys index.")}
                  </p>
                )}
              </td>
              <td className="px-3 py-3 align-top">{activation ? <button className="min-w-0 text-left text-accent underline-offset-2 hover:underline" type="button" onClick={() => onViewLinked(key.id)}><span className="block wrap-anywhere">{displayDeviceName(activation.computer_name, activation.installation_id, unknownDevice)}</span><span className="mt-1 block font-mono text-xs text-muted">{activation.id}</span></button> : <span className="text-muted">{t("search.details.noActiveDevice", "No active Device")}</span>}</td>
              <td className="px-3 py-3 align-top"><Badge variant={activation ? "success" : "neutral"}>{activation ? t("search.details.inUse", "In use") : t("search.details.available", "Available")}</Badge></td>
              <td className="px-3 py-3 align-top"><div className="flex shrink-0 flex-wrap gap-2"><Button variant="ghost" size="sm" onClick={() => onViewLinked(key.id)}>{t("search.details.viewLinkedDevices", "View linked devices")}</Button>
              {key.status === "active" && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!canReplace || busyKeyID === key.id}
                    onClick={() => onOpenReplace(key.id)}
                  >
                    {t("search.details.replaceProductKey", "Replace key")}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={!canRevoke || busyKeyID === key.id}
                    onClick={() => onOpenRevoke(key.id)}
                  >
                    {t("search.details.revokeProductKey", "Revoke key")}
                  </Button>
                </>
              )}
              </div></td>
            </tr>
          );})}
            </tbody>
          </table>
          {hasNextPage && <Button className="w-full" variant="outline" size="sm" disabled={loadingMore} onClick={onLoadMore}>{loadingMore ? t("common.loading", "Loading…") : t("search.details.loadMoreProductKeys", "Load more Product Keys")}</Button>}
        </div>
      )}
    </section>
  );
}

function activationLocation(activation: Activation) {
  return [
    activation.location_city,
    activation.location_region,
    activation.location_country_code,
  ].filter(Boolean).join(", ");
}

function Timeline({
  events,
  loading,
  loadingMore,
  hasNextPage,
  error,
  onLoadMore,
}: {
  events: AuditEvent[];
  loading: boolean;
  loadingMore: boolean;
  hasNextPage: boolean;
  error: unknown;
  onLoadMore: () => void;
}) {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();
  const sorted = [...events].sort(
    (left, right) =>
      new Date(right.created_at).getTime() -
      new Date(left.created_at).getTime(),
  );
  return (
    <section className="rounded-lg border border-line bg-panel px-5 py-4" aria-labelledby="timeline-heading">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 id="timeline-heading" className="font-semibold text-ink">
            {t("search.details.audit", "Audit history")}
          </h3>
          <p className="mt-0.5 text-xs text-muted">
            {t("search.details.timelineDescription", "License and linked Activation events in one context.")}
          </p>
        </div>
        <Badge variant="neutral">
          {hasNextPage
            ? t("search.details.loadedCount", {
                count: sorted.length,
                defaultValue: `${sorted.length} loaded`,
              })
            : t("search.details.eventCount", {
                count: sorted.length,
                defaultValue: `${sorted.length} events`,
              })}
        </Badge>
      </div>
      {Boolean(error) && (
        <p className="mt-3 text-xs text-warning">
          {t("search.details.timelineUnavailable", "Audit timeline is temporarily unavailable.")}
        </p>
      )}
      {loading ? (
        <div
          className="mt-4 h-14 rounded-md bg-panel-raised"
          aria-label={t("search.details.timelineLoading", "Loading Audit timeline")}
          role="status"
        />
      ) : sorted.length === 0 ? (
        <p className="mt-4 text-sm text-muted">
          {t("search.details.timelineEmpty", "No Audit events were returned for this record.")}
        </p>
      ) : (
        <div className="mt-4 divide-y divide-line rounded-md border border-line bg-canvas">
          {sorted.map((event) => (
            <article className="p-3 text-xs" key={event.id}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-ink">
                  {humanizeAction(event.action)}
                </span>
                <time
                  className="font-mono text-xs text-muted"
                  dateTime={event.created_at}
                >
                  {formatDateTime(event.created_at)}
                </time>
              </div>
              <p className="mt-1 text-muted">
                {t("search.details.actorDetails", "Actor {{actor}} / {{role}} · IP {{ip}}", { actor: event.actor_id, role: event.actor_role, ip: event.ip || t("audit.table.notRecorded", "not recorded") })}
              </p>
              {event.reason && (
                <p className="mt-1 text-ink/90">{t("search.details.reason", "Reason")}: {event.reason}</p>
              )}
              <AuditChangeEvidence before={event.before} after={event.after} />
            </article>
          ))}
          {hasNextPage && (
            <div className="p-3">
              <Button
                className="w-full"
                variant="outline"
                size="sm"
                disabled={loadingMore}
                onClick={onLoadMore}
              >
                {loadingMore
                  ? t("common.loading", "Loading…")
                  : t("search.details.loadMoreEvents", "Load more events")}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export { LicenseDetailPage as EditLicensePage };
